import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  IconButton,
  Chip,
  FormGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  CalendarToday as CalendarIcon,
  Receipt as ReceiptIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import SalesReportViewer from './SaleReportViewer';
import { generateSalesReport } from '../services/reportService';

const SalesReportDialog = ({ open, onClose }) => {
  const today = new Date();
  const lastMonth = new Date();
  lastMonth.setMonth(lastMonth.getMonth() - 1);

  const [filters, setFilters] = useState({
    startDate: (() => { const d = new Date(lastMonth); d.setHours(0,0,0,0); return d.toISOString(); })(),
    endDate: new Date().toISOString(),
    documentType: 'all',
    groupBy: 'day',
    paymentMethod: 'all',
    includeVAT: true,
    includeCustomers: true,
    includeProducts: true,
  });
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const handleFilterChange = (field) => (event) => {
    let value = event.target.value;

    if (field === 'startDate') {
      // Always set to start of day in ISO format
      const d = new Date(value);
      d.setHours(0, 0, 0, 0);
      value = d.toISOString();
    } else if (field === 'endDate') {
      const selectedDate = new Date(value);
      const today = new Date();
      const isToday =
        selectedDate.getFullYear() === today.getFullYear() &&
        selectedDate.getMonth() === today.getMonth() &&
        selectedDate.getDate() === today.getDate();

      if (isToday) {
        // Set to current date and time in ISO format
        value = new Date().toISOString();
      } else {
        // Set to end of selected day (23:59:59.999)
        selectedDate.setHours(23, 59, 59, 999);
        value = selectedDate.toISOString();
      }
    }

    setFilters({
      ...filters,
      [field]: value,
    });
  };

  const handleCheckboxChange = (field) => (event) => {
    setFilters({
      ...filters,
      [field]: event.target.checked,
    });
  };

  const handleQuickDate = (range) => {
    const endDate = new Date();
    const startDate = new Date();

    switch (range) {
      case 'today':
        // Start and end are today
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'yesterday':
        startDate.setDate(startDate.getDate() - 1);
        startDate.setHours(0, 0, 0, 0);
        endDate.setDate(endDate.getDate() - 1);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'week':
        startDate.setDate(startDate.getDate() - 7);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'month':
        startDate.setMonth(startDate.getMonth() - 1);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'quarter':
        startDate.setMonth(startDate.getMonth() - 3);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'year':
        startDate.setFullYear(startDate.getFullYear() - 1);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        break;
      default:
        break;
    }

    setFilters({
      ...filters,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    });

    toast.info(`Date range set to: ${range}`);
  };

  const handleGenerate = async () => {
    // Validate dates
    if (!filters.startDate || !filters.endDate) {
      toast.error('Please select both start and end dates');
      return;
    }

    if (new Date(filters.startDate) > new Date(filters.endDate)) {
      toast.error('Start date must be before end date');
      return;
    }

    try {
      setLoading(true);
      
      // Call the actual API
      const data = await generateSalesReport(filters);
      setReportData(data);
      setShowPreview(true);
      toast.success('Report generated successfully!');
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error(error.response?.data?.message || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (format) => {
    toast.info(`Downloading as ${format.toUpperCase()}...`);
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      <Dialog open={open && !showPreview} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 2,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarIcon />
            <Typography variant="h6" fontWeight={700}>
              Sales Report Parameters
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ pt: 3 }}>
          {/* Quick Date Range Buttons */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Quick Date Range:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {[
                { value: 'today', label: 'Today' },
                { value: 'yesterday', label: 'Yesterday' },
                { value: 'week', label: 'Last 7 Days' },
                { value: 'month', label: 'Last 30 Days' },
                { value: 'quarter', label: 'Last 90 Days' },
                { value: 'year', label: 'Last Year' },
              ].map((range) => (
                <Chip
                  key={range.value}
                  label={range.label}
                  onClick={() => handleQuickDate(range.value)}
                  color="primary"
                  variant="outlined"
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'primary.main',
                      color: 'white',
                    },
                  }}
                />
              ))}
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Date Range - Using Native HTML5 Date Input */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={filters.startDate.slice(0, 10)}
                onChange={handleFilterChange('startDate')}
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  max: filters.endDate.slice(0, 10),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={filters.endDate.slice(0, 10)}
                onChange={handleFilterChange('endDate')}
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  min: filters.startDate.slice(0, 10),
                  max: new Date().toISOString().slice(0, 10),
                }}
              />
            </Grid>
          </Grid>

          {/* Document Type Filter - NEW */}
          <Box sx={{ mb: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Document Type</InputLabel>
              <Select
                value={filters.documentType}
                onChange={handleFilterChange('documentType')}
                label="Document Type"
                startAdornment={
                  <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                    {filters.documentType === 'ticket_de_caisse' ? (
                      <ReceiptIcon sx={{ fontSize: 20 }} />
                    ) : filters.documentType === 'invoice' ? (
                      <DescriptionIcon sx={{ fontSize: 20 }} />
                    ) : null}
                  </Box>
                }
              >
                <MenuItem value="all">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography>All Documents</Typography>
                    <Chip label="All" size="small" />
                  </Box>
                </MenuItem>
                <MenuItem value="ticket_de_caisse">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ReceiptIcon sx={{ fontSize: 20, color: '#4caf50' }} />
                    <Typography>Ticket de Caisse</Typography>
                    <Chip label="Receipt" size="small" color="success" />
                  </Box>
                </MenuItem>
                <MenuItem value="invoice">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DescriptionIcon sx={{ fontSize: 20, color: '#2196f3' }} />
                    <Typography>Invoice (Facture)</Typography>
                    <Chip label="Invoice" size="small" color="primary" />
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
            
            {/* Info about selected document type */}
            {filters.documentType !== 'all' && (
              <Box sx={{ mt: 1, p: 1.5, bgcolor: '#e3f2fd', borderRadius: 1 }}>
                <Typography variant="caption" color="primary" fontWeight={600}>
                  📄 Filtering by: {filters.documentType === 'ticket_de_caisse' ? 'Ticket de Caisse only' : 'Invoices only'}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Group By & Payment Method */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Group By</InputLabel>
                <Select
                  value={filters.groupBy}
                  onChange={handleFilterChange('groupBy')}
                  label="Group By"
                >
                  <MenuItem value="day">Daily</MenuItem>
                  <MenuItem value="week">Weekly</MenuItem>
                  <MenuItem value="month">Monthly</MenuItem>
                  <MenuItem value="quarter">Quarterly</MenuItem>
                  <MenuItem value="year">Yearly</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Payment Method</InputLabel>
                <Select
                  value={filters.paymentMethod}
                  onChange={handleFilterChange('paymentMethod')}
                  label="Payment Method"
                >
                  <MenuItem value="all">All Methods</MenuItem>
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="virement">Virement</MenuItem>
                  <MenuItem value="bank">Bank Transfer</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Report Options */}
          <Box>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Report Options:
            </Typography>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filters.includeVAT}
                    onChange={handleCheckboxChange('includeVAT')}
                    color="primary"
                  />
                }
                label="Include VAT Breakdown"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filters.includeCustomers}
                    onChange={handleCheckboxChange('includeCustomers')}
                    color="primary"
                  />
                }
                label="Include Customer Details"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filters.includeProducts}
                    onChange={handleCheckboxChange('includeProducts')}
                    color="primary"
                  />
                }
                label="Include Product Breakdown"
              />
            </FormGroup>
          </Box>

          {/* Info Box */}
          <Box
            sx={{
              mt: 3,
              p: 2,
              bgcolor: '#e3f2fd',
              borderRadius: 2,
              border: '1px solid #2196f3',
            }}
          >
            <Typography variant="caption" fontWeight={600} color="primary" display="block" gutterBottom>
              💡 TIPS:
            </Typography>
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>
              • Use quick date buttons for common periods
            </Typography>
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>
              • Filter by document type to analyze Tickets vs Invoices separately
            </Typography>
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>
              • Select a smaller date range for detailed analysis
            </Typography>
            <Typography variant="caption" display="block" color="text.secondary">
              • Enable VAT breakdown for tax reporting
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button onClick={onClose} variant="outlined" color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleGenerate}
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
            disabled={loading}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              minWidth: 150,
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #6a3d8f 100%)',
              },
            }}
          >
            {loading ? 'Generating...' : 'Generate Report'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Report Viewer Dialog */}
      {showPreview && reportData && (
        <SalesReportViewer
          open={showPreview}
          onClose={() => {
            setShowPreview(false);
            onClose();
          }}
          reportData={reportData}
          filters={filters}
          onDownload={handleDownload}
          onPrint={handlePrint}
        />
      )}
    </>
  );
};

export default SalesReportDialog;